import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type {
  CreateInviteRequestDTO,
  InviteDTO,
  OrganizationForInviteDTO,
  RegisterUserFromInviteRequestDTO,
  UpdateInviteRequestDTO,
  ValidateInviteTokenResponseDTO,
} from "../models/invite_model";

export interface InviteDataSource {
  getInvites(): Promise<InviteDTO[]>;
  getAllInvites(options: QueryOptions): Promise<PaginatedResponse<InviteDTO[]>>;
  getOrganizationsForInvite(id: string): Promise<OrganizationForInviteDTO[]>;
  createInvite(request: CreateInviteRequestDTO): Promise<InviteDTO>;
  updateInvite(id: string, request: UpdateInviteRequestDTO): Promise<InviteDTO>;
  deleteInvite(id: string): Promise<void>;
  resendInvite(id: string): Promise<void>;
  cancelInvite(id: string): Promise<void>;
  validateInvite(token: string): Promise<ValidateInviteTokenResponseDTO>;
  registerFromInvite(request: RegisterUserFromInviteRequestDTO): Promise<void>;
}

export const inviteDataSourceKey: InjectionKey<InviteDataSource> =
  Symbol("InviteDataSource");

export class InviteDataSourceImpl implements InviteDataSource {
  async getInvites(): Promise<InviteDTO[]> {
    const response = await $api<{ data: InviteDTO[] }>("/invites", {
      method: "GET",
    });
    return response.data;
  }

  async getAllInvites(
    options: QueryOptions,
  ): Promise<PaginatedResponse<InviteDTO[]>> {
    const response = await $api<PaginatedResponse<InviteDTO[]>>(
      "/invites/all",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async getOrganizationsForInvite(
    id: string,
  ): Promise<OrganizationForInviteDTO[]> {
    const response = await $api<{ data: OrganizationForInviteDTO[] }>(
      `/accounts/organizations/${id}/child-orgs`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async createInvite(request: CreateInviteRequestDTO): Promise<InviteDTO> {
    const response = await $api<{ data: InviteDTO }>("/invites", {
      method: "POST",
      body: request,
    });
    return response.data;
  }

  async updateInvite(
    id: string,
    request: UpdateInviteRequestDTO,
  ): Promise<InviteDTO> {
    const response = await $api<{ data: InviteDTO }>(`/invites/${id}`, {
      method: "PUT",
      body: request,
    });
    return response.data;
  }

  async deleteInvite(id: string): Promise<void> {
    await $api(`/invites/${id}`, {
      method: "DELETE",
    });
  }

  async resendInvite(id: string): Promise<void> {
    await $api(`/invites/${id}/resend`, {
      method: "PUT",
    });
  }

  async cancelInvite(id: string): Promise<void> {
    await $api(`/invites/${id}/cancel`, {
      method: "PUT",
    });
  }

  async validateInvite(token: string): Promise<ValidateInviteTokenResponseDTO> {
    const response = await $api<{ data: ValidateInviteTokenResponseDTO }>(
      `/invites/validate/${token}`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async registerFromInvite(
    request: RegisterUserFromInviteRequestDTO,
  ): Promise<void> {
    await $api("/invites/register", {
      method: "POST",
      body: request,
    });
  }
}
